import { useState } from 'react';
import { Typography, Card, Row, Col, Tag, Space, Button, Modal, Form, Input, Switch, Descriptions, Badge, Spin, message } from 'antd';
import { AndroidOutlined, AppleOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAppVersions, updateAppVersion } from '@/services/appVersions';

interface AppVersion {
  id: string;
  platform: string;
  minRequiredVersion: string;
  latestVersion: string;
  forceUpdate: boolean;
}

export default function AppVersionsPage() {
  const [editItem, setEditItem] = useState<AppVersion | null>(null);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({ queryKey: ['appVersions'], queryFn: getAppVersions });
  const updateMutation = useMutation({
    mutationFn: ({ id, ...rest }: { id: string; minRequiredVersion?: string; latestVersion?: string; forceUpdate?: boolean }) => updateAppVersion(id, rest),
    onSuccess: () => { message.success('Updated'); queryClient.invalidateQueries({ queryKey: ['appVersions'] }); setEditItem(null); },
    onError: () => message.error('Failed to update'),
  });

  const versions: AppVersion[] = (data as { data?: AppVersion[] })?.data ?? (Array.isArray(data) ? data as AppVersion[] : []);
  const android = versions.find((v) => v.platform === 'android');
  const ios = versions.find((v) => v.platform === 'ios');

  const renderCard = (version: AppVersion | undefined, icon: React.ReactNode, label: string) => (
    <Card title={<Space>{icon} {label}</Space>} extra={version && <Button onClick={() => { setEditItem(version); form.setFieldsValue(version); }}>Edit</Button>}>
      {!version ? <Typography.Text type="secondary">Not configured</Typography.Text> : (
        <Descriptions column={1} size="small">
          <Descriptions.Item label="Min Required">{version.minRequiredVersion}</Descriptions.Item>
          <Descriptions.Item label="Latest">{version.latestVersion}</Descriptions.Item>
          <Descriptions.Item label="Force Update"><Badge status={version.forceUpdate ? 'error' : 'success'} text={version.forceUpdate ? 'Yes' : 'No'} /></Descriptions.Item>
        </Descriptions>
      )}
    </Card>
  );

  if (isLoading) return <Spin size="large" />;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Space align="center">
          <Typography.Title level={4} style={{ margin: 0, color: '#0f172a' }}>App Versions</Typography.Title>
          <Tag color="red" style={{ borderRadius: 20, fontWeight: 700 }}>Super Admin</Tag>
        </Space>
        <div><Typography.Text style={{ color: '#64748b', fontSize: 13 }}>Manage app version policy and force updates</Typography.Text></div>
      </div>
      <Row gutter={16}>
        <Col span={12}>{renderCard(android, <AndroidOutlined />, 'Android')}</Col>
        <Col span={12}>{renderCard(ios, <AppleOutlined />, 'iOS')}</Col>
      </Row>
      <Modal title={`Edit ${editItem?.platform} Version`} open={!!editItem} onCancel={() => setEditItem(null)} onOk={() => form.submit()} confirmLoading={updateMutation.isPending}>
        <Form form={form} layout="vertical" onFinish={(values) => editItem && updateMutation.mutate({ id: editItem.id, ...values })}>
          <Form.Item name="minRequiredVersion" label="Min Required Version" rules={[{ required: true }]}><Input placeholder="1.0.0" /></Form.Item>
          <Form.Item name="latestVersion" label="Latest Version" rules={[{ required: true }]}><Input placeholder="1.2.0" /></Form.Item>
          <Form.Item name="forceUpdate" label="Force Update" valuePropName="checked"><Switch /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
